"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { tryCatch } from "@/utils/validators";
import { Clipboard, Copy, CopyCheck } from "lucide-react";
import { useTheme } from "next-themes";
import dynamic from "next/dynamic";
import { ReactNode, useState } from "react";
import Confetti from "react-dom-confetti";
import { EmailIcon, EmailShareButton, FacebookIcon, FacebookShareButton, LinkedinIcon, LinkedinShareButton, RedditIcon, RedditShareButton, TwitterShareButton, WhatsappIcon, WhatsappShareButton, XIcon } from "react-share";
import { toast } from "sonner";
import QRCodeContainer from "../fancy/qr-code-container";
import LoadingFallback from "../layout/loading-fallback";
const QRLine = dynamic(() => import("react-qrbtf").then(mod => mod.QRLine), {
  ssr: false,
  loading: () => <LoadingFallback />
});
type ShareLinkDialogProps = {
  title: string;
  description: string;
  shareUrl: string;
  onShareAction?: () => Promise<void>;
  children?: ReactNode;
};
export function ShareLinkDialog(t0) {
  const $ = _c(72);
  const {
    title,
    description,
    shareUrl,
    onShareAction,
    children
  } = t0;
  const {
    resolvedTheme
  } = useTheme();
  const [showConfetti, setShowConfetti] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = async function handleCopyToClipboard(text) {
      const {
        error
      } = await tryCatch(navigator.clipboard.writeText(text));
      if (error) {
        toast.error("Could not copy share URL to the clipboard.");
        return;
      }
      toast.success("Link copied to the clipboard.");
      setIsCopied(true);
      setShowConfetti(true);
      setTimeout(() => {
        setIsCopied(false);
        setShowConfetti(false);
      }, 3000);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleCopyToClipboard = t1;
  let t2;
  if ($[1] !== children) {
    t2 = <DialogTrigger asChild={true}>{children ? <div>{children}</div> : <Button variant="outline" size="icon"><Clipboard size={18} /></Button>}</DialogTrigger>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== title) {
    t3 = <DialogTitle>{title}</DialogTitle>;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== description) {
    t4 = <DialogDescription>{description}</DialogDescription>;
    $[5] = description;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <DialogHeader>{t3}{t4}</DialogHeader>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label htmlFor="link" className="sr-only">Link</Label>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== shareUrl) {
    t7 = <div className="flex items-center space-x-2"><div className="grid flex-1 gap-2">{t6}<Input id="link" defaultValue={shareUrl} readOnly={true} /></div></div>;
    $[11] = shareUrl;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <FacebookIcon size={32} />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onShareAction || $[15] !== shareUrl) {
    t9 = <FacebookShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t8}</FacebookShareButton>;
    $[14] = onShareAction;
    $[15] = shareUrl;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <WhatsappIcon size={32} />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== onShareAction || $[19] !== shareUrl) {
    t11 = <WhatsappShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t10}</WhatsappShareButton>;
    $[18] = onShareAction;
    $[19] = shareUrl;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <XIcon size={32} />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== onShareAction || $[23] !== shareUrl) {
    t13 = <TwitterShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t12}</TwitterShareButton>;
    $[22] = onShareAction;
    $[23] = shareUrl;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <LinkedinIcon size={32} />;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== onShareAction || $[27] !== shareUrl) {
    t15 = <LinkedinShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t14}</LinkedinShareButton>;
    $[26] = onShareAction;
    $[27] = shareUrl;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <RedditIcon size={32} />;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== onShareAction || $[31] !== shareUrl) {
    t17 = <RedditShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t16}</RedditShareButton>;
    $[30] = onShareAction;
    $[31] = shareUrl;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <EmailIcon size={32} />;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== onShareAction || $[35] !== shareUrl) {
    t19 = <EmailShareButton url={shareUrl} className="overflow-hidden rounded-full" onShareWindowClose={onShareAction}>{t18}</EmailShareButton>;
    $[34] = onShareAction;
    $[35] = shareUrl;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t11 || $[38] !== t13 || $[39] !== t15 || $[40] !== t17 || $[41] !== t19 || $[42] !== t9) {
    t20 = <div className="flex items-center gap-1.5 text-center">{t9}{t11}{t13}{t15}{t17}{t19}</div>;
    $[37] = t11;
    $[38] = t13;
    $[39] = t15;
    $[40] = t17;
    $[41] = t19;
    $[42] = t9;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== onShareAction || $[45] !== shareUrl) {
    t21 = async () => {
      handleCopyToClipboard(shareUrl);
      if (onShareAction) {
        await onShareAction();
      }
    };
    $[44] = onShareAction;
    $[45] = shareUrl;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== isCopied) {
    t22 = isCopied ? <CopyCheck size={16} /> : <Copy size={16} />;
    $[47] = isCopied;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span>Copy link</span>;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== showConfetti) {
    t24 = <Confetti active={showConfetti} />;
    $[50] = showConfetti;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t21 || $[53] !== t22 || $[54] !== t24) {
    t25 = <Button variant="outline" size="sm" type="submit" className="flex items-center gap-2 px-3 pr-2" onClick={t21}>{t22}{t23}{t24}</Button>;
    $[52] = t21;
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t20 || $[57] !== t25) {
    t26 = <div className="flex w-fit items-center gap-2 py-2">{t20}{t25}</div>;
    $[56] = t20;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const t27 = resolvedTheme === "light" ? "#000000" : "#ffffff";
  const t28 = resolvedTheme === "light" ? "#000000" : "#ffffff";
  let t29;
  if ($[59] !== shareUrl || $[60] !== t27 || $[61] !== t28) {
    t29 = <QRCodeContainer className="size-52"><QRLine value={shareUrl} direction="h-v" posType="roundRect" className="scale-125 bg-transparent" icon="/images/logo/solid/logo-black.svg" iconScale={0.4} posColor={t27} lineColor={t28} /></QRCodeContainer>;
    $[59] = shareUrl;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <DialogFooter className="sm:justify-start"><DialogClose asChild={true}><Button variant="outline">Close</Button></DialogClose></DialogFooter>;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] !== t26 || $[65] !== t29 || $[66] !== t5 || $[67] !== t7) {
    t31 = <DialogContent className="sm:max-w-md">{t5}{t7}{t26}{t29}{t30}</DialogContent>;
    $[64] = t26;
    $[65] = t29;
    $[66] = t5;
    $[67] = t7;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== t2 || $[70] !== t31) {
    t32 = <Dialog>{t2}{t31}</Dialog>;
    $[69] = t2;
    $[70] = t31;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  return t32;
}

// Recycled

// icon="/images/popular-brand-icons/v0.png"